import React, { useState } from 'react';
import { useApp } from '@/context/AppContext';
import { PageHeader, Card } from '@/components/common';
import { formatCurrency } from '@/lib/format';
import { Slider } from '@/components/ui/slider';
import { RotateCcw, TrendingUp, Wallet } from 'lucide-react';

const ScenarioPlanner = () => {
  const { t, lang, data } = useApp();
  const baseRevenue = data.finance.kpis[0].value;
  const baseProfit = data.finance.kpis[2].value;
  const baseCash = data.cash.available;

  const [rev, setRev] = useState(0);
  const [cost, setCost] = useState(0);
  const [hires, setHires] = useState(0);
  const [invest, setInvest] = useState(0);

  const hireCost = hires * 45000;
  const newRevenue = baseRevenue * (1 + rev / 100);
  const newExpenses = (baseRevenue - baseProfit) * (1 + cost / 100) + hireCost;
  const projectedProfit = Math.round(newRevenue - newExpenses);
  const projectedCash = Math.round(baseCash + (projectedProfit - baseProfit) - invest * 1000);
  const affordable = projectedCash > 100000 && projectedProfit > 0;

  const reset = () => { setRev(0); setCost(0); setHires(0); setInvest(0); };

  const sliders = [
    { label: t('scenario.revenueChange'), value: rev, set: setRev, min: -30, max: 50, suffix: '%', tid: 'slider-revenue' },
    { label: t('scenario.costChange'), value: cost, set: setCost, min: -20, max: 40, suffix: '%', tid: 'slider-cost' },
    { label: t('scenario.hires'), value: hires, set: setHires, min: 0, max: 10, suffix: '', tid: 'slider-hires' },
    { label: t('scenario.investment'), value: invest, set: setInvest, min: 0, max: 300, suffix: 'k€', tid: 'slider-investment' },
  ];

  return (
    <div>
      <PageHeader title={t('scenario.title')} subtitle={t('scenario.subtitle')} actions={
        <button onClick={reset} data-testid="scenario-reset" className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-white/80 bg-white/[0.96] backdrop-blur-xl text-sm font-medium text-zinc-700 hover:bg-white transition-colors">
          <RotateCcw className="w-4 h-4" /> {t('scenario.reset')}
        </button>
      } />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <Card className="lg:col-span-3 p-6 space-y-7" data-testid="scenario-controls">
          {sliders.map((s) => (
            <div key={s.tid}>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-zinc-700">{s.label}</span>
                <span className="font-mono-num text-sm font-bold text-cyan-600" data-testid={`${s.tid}-value`}>{s.value > 0 && s.suffix === '%' ? '+' : ''}{s.value}{s.suffix}</span>
              </div>
              <Slider value={[s.value]} min={s.min} max={s.max} step={1} onValueChange={(v) => s.set(v[0])} data-testid={s.tid} />
            </div>
          ))}
        </Card>

        <Card className="lg:col-span-2 p-6 text-white relative overflow-hidden" style={{ background: 'linear-gradient(150deg, #0F172A 0%, #1E1145 100%)' }} data-testid="scenario-result">
          <div className="grain absolute inset-0 opacity-20" />
          <div className="relative">
            <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-white/50 mb-1">{t('scenario.result')}</p>
            <p className="font-heading text-2xl font-bold mb-6">{t('scenario.question')}</p>

            <div className="space-y-5">
              <div>
                <div className="flex items-center gap-2 text-white/60 text-xs mb-1"><TrendingUp className="w-3.5 h-3.5" />{t('scenario.projectedProfit')}</div>
                <p className={`font-heading text-3xl font-bold font-mono-num ${projectedProfit >= 0 ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="scenario-profit">{formatCurrency(projectedProfit, lang)}</p>
              </div>
              <div>
                <div className="flex items-center gap-2 text-white/60 text-xs mb-1"><Wallet className="w-3.5 h-3.5" />{t('scenario.projectedCash')}</div>
                <p className={`font-heading text-3xl font-bold font-mono-num ${projectedCash >= 0 ? 'text-white' : 'text-rose-400'}`} data-testid="scenario-cash">{formatCurrency(projectedCash, lang)}</p>
              </div>
            </div>

            <div className={`mt-7 rounded-xl px-4 py-3 text-sm font-medium ${affordable ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'}`} data-testid="scenario-verdict">
              {affordable
                ? (lang === 'es' ? '✓ Puedes permitírtelo manteniendo tu colchón de seguridad.' : '✓ You can afford it while keeping your safety buffer.')
                : (lang === 'es' ? '⚠ Este escenario comprometería tu liquidez.' : '⚠ This scenario would compromise your liquidity.')}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default ScenarioPlanner;
